import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, inject, isDevMode } from "@angular/core";
import { LuxonDateAdapter } from "@angular/material-luxon-adapter";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { BrowserAnimationsModule, provideAnimations } from "@angular/platform-browser/animations";
import { PreloadAllModules, provideRouter, withInMemoryScrolling, withPreloading } from "@angular/router";
import { provideFuse } from "@fuse";
import { provideTransloco, TranslocoService } from "@ngneat/transloco";
import { QRCodeModule } from "angularx-qrcode";
import { appRoutes } from "app/app.routes";
import { provideAuth } from "app/core/auth/auth.provider";
import { SessionManagerService } from "app/core/auth/session-manager.service";
import { UserProfileService } from "app/core/user/user-profile.service";
import { getThemeForCurrentOrganization } from "app/core/theme/organization-theme.config";
import { provideIcons } from "app/core/icons/icons.provider";
import { mockApiServices } from "app/mock-api";
import { NgxMapboxGLModule } from "ngx-mapbox-gl";
import { provideToastr } from "ngx-toastr";
import { firstValueFrom } from "rxjs";
import { environment } from "../environments/environment";
import { TranslocoHttpLoader } from "./core/transloco/transloco.http-loader";
import { AppConfigInterceptor } from "./interceptors/app-config.interceptor";
import { ErrorInterceptor } from "./interceptors/error-interceptor.service";

export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(BrowserAnimationsModule, QRCodeModule),
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: AppConfigInterceptor, multi: true },
        NgxMapboxGLModule.withConfig({
            accessToken: environment.mapboxKey,
        }).providers,
        provideAnimations(),
        provideToastr({
            positionClass: "toast-bottom-right",
        }),
        provideRouter(appRoutes, withPreloading(PreloadAllModules), withInMemoryScrolling({ scrollPositionRestoration: "enabled" })),
        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_LOCALE,
            useValue: "es-ES",
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: "D",
                },
                display: {
                    dateInput: "dd/MM/yyyy", // this is how your date will get displayed on the Input
                    monthYearLabel: "MMMM yyyy",
                    dateA11yLabel: "LL",
                    monthYearA11yLabel: "MMMM yyyy",
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: "en",
                        label: "English",
                    },
                    {
                        id: "es",
                        label: "Español",
                    },
                    {
                        id: "tr",
                        label: "Türkçe",
                    },
                    {
                        id: "pt",
                        label: "Português",
                    },
                ],
                defaultLang: "en",
                fallbackLang: "en",
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
                // `fallbackLang` por sí solo no rellena claves: solo entra en juego si falla la
                // carga del idioma entero. Sin `useFallbackTranslation`, una clave que existe en
                // en.json pero no en pt.json se pinta cruda al usuario ("campaign.pacing_strategy").
                // Con esto activado cae al inglés, que es texto legible.
                missingHandler: {
                    useFallbackTranslation: true,
                    logMissingKey: true, // solo escribe en consola fuera de prodMode
                    allowEmpty: false,
                },
            },
            loader: TranslocoHttpLoader,
        }),
        {
            // Resolve preferred language: from localStorage if present, else GET user on first device login
            provide: APP_INITIALIZER,
            useFactory: () => {
                const translocoService = inject(TranslocoService);
                const userProfileService = inject(UserProfileService);
                const sessionManager = inject(SessionManagerService);

                return () => {
                    // Kill switch: wipe pre-multi-session storage so those users
                    // get a clean sign-in instead of staying stuck on stale state.
                    const wiped = sessionManager.ensureSchemaVersion();

                    // Migrate from single-session to multi-session format (idempotent)
                    if (!wiped) {
                        sessionManager.migrateFromLegacy();
                    }

                    const accessToken = localStorage.getItem("accessToken");
                    const defaultLang = translocoService.getDefaultLang();
                    let langToUse = defaultLang;

                    if (accessToken) {
                        const fromStorage = userProfileService.getPreferredLanguageFromStorage();
                        if (fromStorage) {
                            langToUse = fromStorage;
                        } else {
                            const storedUserRaw = localStorage.getItem("user");
                            if (storedUserRaw) {
                                try {
                                    const storedUser = JSON.parse(storedUserRaw) as { preferred_language?: unknown };
                                    const preferredLanguage =
                                        typeof storedUser.preferred_language === "string" ? storedUser.preferred_language : undefined;
                                    const translocoLang = userProfileService.toTranslocoLang(preferredLanguage);
                                    if (translocoLang && preferredLanguage) {
                                        langToUse = translocoLang;
                                        userProfileService.setPreferredLanguageInStorage(preferredLanguage);
                                    }
                                } catch {
                                    // Ignore malformed user storage and keep default language.
                                }
                            }
                        }
                    }

                    translocoService.setActiveLang(langToUse);
                    return firstValueFrom(translocoService.load(langToUse));
                };
            },
            multi: true,
        },

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            fuse: {
                app: {
                    name: "GeoActivate",
                },
                layout: "dense",
                scheme: "light",
                screens: {
                    sm: "600px",
                    md: "960px",
                    lg: "1280px",
                    xl: "1440px",
                },
                theme: getThemeForCurrentOrganization(),
                themes: [
                    {
                        id: "theme-default",
                        name: "Default",
                    },
                    {
                        id: "theme-brand",
                        name: "Brand",
                    },
                    {
                        id: "theme-teal",
                        name: "Teal",
                    },
                    {
                        id: "theme-rose",
                        name: "Rose",
                    },
                    {
                        id: "theme-purple",
                        name: "Purple",
                    },
                    {
                        id: "theme-amber",
                        name: "Amber",
                    },
                ],
            },
        }),
    ],
};
